/**
 * ModuleRoute — Bloquea una ruta si el módulo correspondiente está desactivado.
 *
 * Lee `useBrand().isEnabled(id)` y si la data ya cargó y el módulo está off,
 * muestra una pantalla 403 con CTA hacia Ajustes → Módulos.
 *
 * Uso:
 *   <ModuleRoute module="fixed_assets">
 *     <AssetsPage />
 *   </ModuleRoute>
 */
import { useBrand } from "@/context/BrandContext";
import { useAuth } from "@/contexts/AuthContext";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { PackageX, Settings as SettingsIcon, Loader2 } from "lucide-react";
import { PageContainer } from "@/components/PageHeader";

export default function ModuleRoute({ module: moduleId, children }) {
  const { isEnabled, loading } = useBrand();
  const { user } = useAuth();

  // Mientras carga, NO renderizamos los children (podrían disparar fetches a
  // endpoints gated → 403 ruidoso). Mostramos un placeholder.
  if (loading) {
    return (
      <PageContainer>
        <div className="flex items-center justify-center py-24" data-testid="module-loading">
          <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
        </div>
      </PageContainer>
    );
  }
  if (isEnabled(moduleId)) return children;

  const isAdmin = (user?.role || "").toLowerCase() === "admin";

  return (
    <PageContainer>
      <div className="max-w-xl mx-auto mt-16 text-center space-y-4" data-testid="module-disabled">
        <div className="w-16 h-16 mx-auto rounded-full bg-amber-100 dark:bg-amber-950 flex items-center justify-center">
          <PackageX className="w-8 h-8 text-amber-600" />
        </div>
        <div>
          <h2 className="text-xl font-bold">Módulo desactivado</h2>
          <p className="text-sm text-muted-foreground mt-2">
            Esta función forma parte de un módulo que no está activado para tu empresa.
          </p>
        </div>
        {isAdmin ? (
          <Button asChild data-testid="go-to-modules-btn">
            <Link to="/settings?tab=modules">
              <SettingsIcon className="w-4 h-4 mr-2" />
              Activar en Ajustes → Módulos
            </Link>
          </Button>
        ) : (
          <p className="text-xs text-muted-foreground">
            Contacta al administrador para activar este módulo.
          </p>
        )}
      </div>
    </PageContainer>
  );
}
